import React from 'react';
import { MapMarkerText } from 'components/map/components/MapMarkerText';
import { CONSTANTS } from 'modules/admin-panel/utils';
import {
	TrendsTypes,
	ICommentsAverage,
	SatisfactionLevelTypes,
	IResultMapPointCalculated,
} from 'modules/admin-panel/pages/results/models';
import { SATISFACTION_LEVELS } from 'modules/admin-panel/pages/results/utils';
import { createCoordinatesMap } from './creatMapMap';
import { getMapIcon } from './getMapIcon';

const checkScoreBelongsToAbsoluteType = (
	score: number,
	activeType: TrendsTypes | SatisfactionLevelTypes,
): boolean => {
	switch (activeType) {
		case SATISFACTION_LEVELS.veryPoor.type:
			return score <= SATISFACTION_LEVELS.veryPoor.max;
		case SATISFACTION_LEVELS.poor.type:
			return (
				score >= SATISFACTION_LEVELS.poor.min &&
				score <= SATISFACTION_LEVELS.poor.max
			);
		case SATISFACTION_LEVELS.ok.type:
			return (
				score >= SATISFACTION_LEVELS.ok.min &&
				score <= SATISFACTION_LEVELS.ok.max
			);
		case SATISFACTION_LEVELS.good.type:
			return (
				score >= SATISFACTION_LEVELS.good.min &&
				score <= SATISFACTION_LEVELS.good.max
			);
		case SATISFACTION_LEVELS.great.type:
			return (
				score >= SATISFACTION_LEVELS.great.min &&
				score <= SATISFACTION_LEVELS.great.max
			);
		default:
			return false;
	}
};

const checkScoreBelongsToTrendType = (
	score: number,
	prevScore: number,
	activeType: TrendsTypes | SatisfactionLevelTypes,
) => {
	const isNegative = score + CONSTANTS.noMatterDelta < prevScore;
	const isPositive = score > prevScore + CONSTANTS.noMatterDelta;

	switch (activeType) {
		case TrendsTypes.negative:
			return isNegative;
		case TrendsTypes.positive:
			return isPositive;
		case TrendsTypes.neutral:
			return !isNegative && !isPositive;
		default:
			return false;
	}
};

export const getResultMarkerList = (
	mapPrev: IResultMapPointCalculated[] | undefined,
	map: IResultMapPointCalculated[] | undefined,
	activeRateType: TrendsTypes | SatisfactionLevelTypes,
	isTrendViewType: boolean,
	activeServiceName: string | undefined,
) => {
	const filterIndexMap: Record<string, boolean> = {};
	const mapPrevCoordinatesMap = createCoordinatesMap(mapPrev);
	const filteredMapItems = map?.filter(
		({
			satisfaction,
			latitude,
			longitude,
			submitted,
		}: IResultMapPointCalculated) => {
			const mapKey = `${latitude}${longitude}`;
			const prevMapItem =
				mapPrevCoordinatesMap && mapPrevCoordinatesMap[mapKey];

			if (submitted === 0) {
				filterIndexMap[mapKey] = true;
				return false;
			}

			if (
				activeRateType === TrendsTypes.all ||
				activeRateType === SATISFACTION_LEVELS.all.type
			)
				return true;

			if (satisfaction === null) {
				return false;
			}

			if (isTrendViewType) {
				if (!prevMapItem) {
					filterIndexMap[mapKey] = true;

					return false;
				}

				const result = checkScoreBelongsToTrendType(
					satisfaction,
					prevMapItem.satisfaction || 0,
					activeRateType,
				);

				if (!result) filterIndexMap[mapKey] = true;

				return result;
			}

			return checkScoreBelongsToAbsoluteType(satisfaction, activeRateType);
		},
	);

	return filteredMapItems?.map(
		({
			submitted,
			satisfaction,
			city,
			office,
			latitude,
			longitude,
			...rest
		}: IResultMapPointCalculated): ICommentsAverage => {
			const mapKey = `${latitude}${longitude}`;
			const prevMapItem =
				mapPrevCoordinatesMap && mapPrevCoordinatesMap[mapKey];
			const prevSatisfaction = isTrendViewType
				? prevMapItem?.satisfaction
				: undefined;
			const icon = getMapIcon(satisfaction, prevSatisfaction);

			const text = (
				<MapMarkerText
					satisfaction={satisfaction}
					prevSatisfaction={prevMapItem?.satisfaction}
					city={city}
					office={office}
					submitted={submitted}
					serviceName={activeServiceName}
					showCustomText={
						isTrendViewType && prevMapItem && satisfaction !== null
					}
				/>
			);

			return {
				count: submitted,
				avg: satisfaction,
				city,
				office,
				text,
				icon,
				textOnly: true,
				latitude,
				longitude,
				...rest,
			};
		},
	);
};
